<template>
  <div
    ref="pageContainer"
    class="page-container"
    :class="{
      'first-page': isFirstSpread,
      'last-page': isLastSpread,
      'embed-viewer': isEmbed,
    }"
  >
    <section v-if="isLastSpread && !isEmbed">
      <BrochurePageAdvertisement :placement="AdDisplayAt.BrochureEndLeft" />
    </section>
    <div v-else-if="!page && isFirstSpread && !isEmbed" class="first-page-ad">
      <Advertisement :display-at="AdDisplayAt.BrochureStart" />
      <Advertisement :display-at="AdDisplayAt.BrochureStartBottom" />
    </div>

    <div v-if="page" ref="pageRef" class="page">
      <BaseImageExternal
        ref="brochureImage"
        :src="page.image_large!"
        class="page-image"
        :lazy="isLazy"
        @load="imageLoaded = true"
      />
      <div v-if="!isLazy" class="hotspots">
        <BrochureHotspotItem
          v-for="hotspot in data?.hotspotsByPage"
          :key="hotspot.id"
          :hotspot="hotspot"
          :hotspot-mode="hotspotMode"
        />
      </div>
    </div>

    <section v-if="isLastSpread && !isEmbed">
      <BrochurePageAdvertisement :placement="AdDisplayAt.BrochureEndRight" />
    </section>
    <BrochurePageAdvertisement
      class="full-page scroll-y"
      :placement="AdDisplayAt.BrochureEndRight"
      v-if="!page && isLastPage && carousel && !isEmbed"
    />
  </div>
</template>

<script setup lang="ts">
import {
  HotspotMode,
  type PageFragment,
  useHotspotsByPageQuery,
  AdDisplayAt,
} from "~/graphql/generated";

const props = defineProps<{
  page: PageFragment | null;
  hotspotMode: HotspotMode;
  isFirstSpread?: boolean;
  isLastSpread?: boolean;
  isLastPage?: boolean;
  carousel?: HTMLDivElement | null;
  onIsInView?: (_: number | null) => void;
}>();

const pagenumber = useRouteNumberParam("pagenumber", 0);
const isEmbed = isEmbedded();

// Lazy loading 'hack' to make sure the image is loaded before going into the viewport
// But only when that page is within 3 pages of the current page
const isLazy = computed(() => {
  if (!props.page) return true;

  return !(
    props.page?.index + 3 >= pagenumber.value &&
    props.page.index - 3 <= pagenumber.value
  );
});

// Called when the image is loaded, so we can show the hotspots
const imageLoaded = ref(false);

// We need the position, because it scales to be contained within the page
// Otherwise we cannot place the hotspots correctly
const pageRef = ref<HTMLDivElement | null>(null);
const { x: pageX, y: pageY } = useElementBounding(pageRef);
const brochureImage = ref<HTMLImageElement | null>(null);
const { x, y, width, height } = useElementBounding(brochureImage);

const imageX = computed(() => `${x.value - pageX.value}px`);
const imageY = computed(() => `${y.value - pageY.value}px`);
const imageWidth = computed(() => `${width.value}px`);
const imageHeight = computed(() => `${height.value}px`);

// An intersection observer that
// 1. sets the active page when the page is fully visible
// 2. Loads the src of the image when it is near the viewport
const pageContainer = ref<HTMLDivElement | null>(null);

// Only start the observer if we have a callback for it (spread does not use intersection observer)
if (props.onIsInView) {
  useIntersectionObserver(
    pageContainer,
    (entries) => {
      entries.forEach((entry) => {
        if (!entry || !entry.isIntersecting) return;

        // If we are fully visible, set the active page
        if (props.onIsInView && entry.intersectionRatio >= 0.9) {
          if (props.page) {
            props.onIsInView(props.page.index);
          } else {
            // The last page is in view
            props.onIsInView(null);
          }
        }
      });
    },
    {
      immediate: true,
      root: props.carousel,
      threshold: [0.9],
    },
  );
}

// Only show hotspots if the page (image) is loaded
const { data } = await useHotspotsByPageQuery({
  pause: !imageLoaded,
  variables: {
    hotspotsInput: {
      pageId: props.page?.id || "",
    },
  },
});
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .page {
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    position: relative;

    .hotspots {
      position: absolute;
      top: v-bind(imageY);
      left: v-bind(imageX);
      width: v-bind(imageWidth);
      height: v-bind(imageHeight);
    }
  }
  &.page-left .page {
    justify-content: end;
  }

  &.page-right .page {
    justify-content: start;
  }

  &.single-page {
    width: 100vw;
    max-height: 100%;
    overflow: unset;
    .page {
      width: 100vw;
    }
  }

  .first-page-ad {
    width: 100%;
    height: 100%;
    background-color: $light-85;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }

  &.last-page {
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    grid-template-rows: 100%;

    &.page {
      grid-column: 2 / 4;
    }

    &.embed-viewer {
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
